import React, { useEffect, useState } from 'react';
import StockService from '../Api_Module/Api_Services/StockService';
import { useNavigate } from 'react-router-dom';

interface MoverItem {
  symbol: string;
  name: string;
  price: number;
  changePercent: number;
}

function normalize(item: unknown): MoverItem | null {
  if (!item || typeof item !== 'object') return null;
  const rec = item as Record<string, unknown>;
  const symbol = typeof rec.symbol === 'string' ? rec.symbol.trim() : '';
  if (!symbol) return null;
  const rawPrice = rec.price;
  const price = typeof rawPrice === 'number' ? rawPrice : parseFloat(String(rawPrice)) || 0;
  let pct = 0;
  if (rec.changesPercentage !== undefined) pct = typeof rec.changesPercentage === 'number' ? rec.changesPercentage : parseFloat(String(rec.changesPercentage)) || 0;
  else if (rec.changePercent !== undefined) pct = typeof rec.changePercent === 'number' ? rec.changePercent : parseFloat(String(rec.changePercent)) || 0;
  else if (rec.change !== undefined) pct = typeof rec.change === 'number' ? rec.change : parseFloat(String(rec.change)) || 0;
  const name = typeof rec.name === 'string' ? rec.name : symbol;
  return { symbol, name, price, changePercent: pct };
}

const TopMoversInline: React.FC = () => {
  const [gainers, setGainers] = useState<MoverItem[]>([]);
  const [losers, setLosers] = useState<MoverItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 640px)');
    const handler = () => setIsMobile(mq.matches);
    handler();
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const [g, l] = await Promise.all([
          StockService.getMarketMovers('gainers').catch(() => null),
          StockService.getMarketMovers('losers').catch(() => null)
        ]);
        if (cancelled) return;
        const gs = Array.isArray(g?.data || g) ? (g?.data || g).map(normalize).filter(Boolean) as MoverItem[] : [];
        const ls = Array.isArray(l?.data || l) ? (l?.data || l).map(normalize).filter(Boolean) as MoverItem[] : [];
        setGainers(gs.slice(0,10));
        setLosers(ls.slice(0,10));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  const formatPrice = (p:number) => `$${p.toFixed(p < 10 ? 2 : 2)}`;
  const formatPct = (p:number) => `${p > 0 ? '+' : ''}${p.toFixed(2)}%`;

  const gainersToShow = isMobile ? gainers.slice(0,5) : gainers;
  const losersToShow = isMobile ? losers.slice(0,5) : losers;

  return (
    <div className="space-y-4 w-full">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold tracking-wide text-theme-secondary">Top Movers Today</h2>
        <button onClick={() => navigate('/market-movers')} className="text-xs text-blue-400 hover:text-blue-300">View all →</button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Gainers */}
        <div className="rounded-lg border border-theme-tertiary/40 bg-theme-secondary/40 backdrop-blur-sm">
          <div className="px-3 py-2 flex items-center justify-between">
            <span className="text-xs font-semibold text-emerald-400">Gainers</span>
          </div>
          <div className="px-3 pb-2">
            {loading && <div className="text-[11px] text-theme-muted py-2">Loading...</div>}
            {!loading && gainersToShow.length === 0 && <div className="text-[11px] text-theme-muted py-2">No data</div>}
            {!loading && gainersToShow.map(g => (
              <button key={g.symbol} onClick={() => navigate(`/${g.symbol}/summary`)} className="w-full flex items-center justify-between py-1.5 text-left group">
                <span className="text-[11px] font-medium truncate max-w-[70px] group-hover:text-blue-300 text-blue-400">{g.symbol}</span>
                <span className="hidden xl:inline text-[11px] text-theme-muted truncate max-w-[90px]">{g.name}</span>
                <span className="text-[11px] tabular-nums text-theme-secondary">{formatPrice(g.price)}</span>
                <span className="text-[11px] font-semibold text-emerald-400">{formatPct(g.changePercent)}</span>
              </button>
            ))}
          </div>
        </div>
        {/* Losers */}
        <div className="rounded-lg border border-theme-tertiary/40 bg-theme-secondary/40 backdrop-blur-sm">
          <div className="px-3 py-2 flex items-center justify-between">
            <span className="text-xs font-semibold text-red-400">Losers</span>
          </div>
          <div className="px-3 pb-2">
            {loading && <div className="text-[11px] text-theme-muted py-2">Loading...</div>}
            {!loading && losersToShow.length === 0 && <div className="text-[11px] text-theme-muted py-2">No data</div>}
            {!loading && losersToShow.map(l => (
              <button key={l.symbol} onClick={() => navigate(`/${l.symbol}/summary`)} className="w-full flex items-center justify-between py-1.5 text-left group">
                <span className="text-[11px] font-medium truncate max-w-[70px] group-hover:text-blue-300 text-blue-400">{l.symbol}</span>
                <span className="hidden xl:inline text-[11px] text-theme-muted truncate max-w-[90px]">{l.name}</span>
                <span className="text-[11px] tabular-nums text-theme-secondary">{formatPrice(l.price)}</span>
                <span className="text-[11px] font-semibold text-red-400">{formatPct(l.changePercent)}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TopMoversInline;
